/**
 * FASE E — Documento do atleta em UM CAMPO SÓ (CPF **ou** RG).
 *
 * O campo é "inteligente": ele reconhece sozinho o que está sendo digitado.
 *   * CPF → máscara `000.000.000-00` + validação dos DÍGITOS VERIFICADORES;
 *   * RG  → máscara LEVE (maiúsculas, espaços normalizados), sem forçar formato,
 *           porque cada estado tem o seu (7 a 9 dígitos, alguns com letra) e o
 *           RG NÃO tem dígito verificador nacional.
 *
 * Duas perguntas diferentes (as duas importam):
 *   * MODO (decide a máscara) — começou com letra → RG; começou com número →
 *     CPF. Quem sabe que o número é RG clica em "é RG" e o campo troca;
 *   * O QUE É DE FATO (decide onde o dado é gravado) — letra → RG; 11 dígitos →
 *     CPF (com aviso se o verificador não bater); até 10 dígitos → RG.
 *
 * Nada BLOQUEIA: tudo o que a validação produz é AVISO e o CPF/RG é OPCIONAL.
 * Funções PURAS (sem React/DOM) e espelhadas em `backend/server.py`
 * (only_digits / format_cpf / is_valid_cpf / clean_rg): uma regra só.
 */

/** Só os dígitos de um texto (é assim que o CPF fica gravado). */
export function onlyDigits(value) {
  return String(value ?? '').replace(/\D/g, '');
}

/** Máscara do CPF — funciona parcialmente enquanto o usuário digita. */
export function formatCpf(value) {
  const d = onlyDigits(value).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/** Validação real do CPF (11 dígitos + 2 verificadores). Nunca bloqueia nada. */
export function isValidCpf(value) {
  const d = onlyDigits(value);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  for (const tamanho of [9, 10]) {
    let soma = 0;
    for (let i = 0; i < tamanho; i += 1) {
      soma += Number(d[i]) * (tamanho + 1 - i);
    }
    let resto = (soma * 10) % 11;
    if (resto === 10) resto = 0;
    if (resto !== Number(d[tamanho])) return false;
  }
  return true;
}

/** Tem letra? (letra em documento = RG, porque o CPF é só número.) */
export function temLetra(value) {
  return /[A-Za-z]/.test(String(value ?? ''));
}

/**
 * Máscara LEVE do RG (usada ENQUANTO o usuário digita): maiúsculas e espaços
 * normalizados, sem forçar formato. O espaço no fim é PRESERVADO de propósito —
 * é ele que deixa o usuário digitar "MG 12345678" sem o espaço sumir na hora.
 */
export function formatRg(value) {
  return String(value ?? '').replace(/\s+/g, ' ').trimStart().toUpperCase().slice(0, 20);
}

/**
 * Valor FINAL do RG (o que vale como dado): o mesmo da máscara leve, sem sobra
 * nas pontas. Espelha `clean_rg` do servidor — uma regra só para os dois lados.
 */
export function cleanRg(value) {
  return formatRg(value).trim();
}

/**
 * O que o campo ENTENDEU do que está digitado.
 * Devolve `{ tipo, digitos, valido, aviso }`:
 *   tipo   → 'cpf' | 'rg' | 'indefinido' | null (campo vazio/sem conteúdo);
 *   valido → true/false para CPF; null quando não há como validar (RG).
 */
export function detectDocumento(value) {
  const texto = String(value ?? '').trim();
  const digitos = onlyDigits(texto).length;
  const letra = temLetra(texto);

  if (!texto || (!digitos && !letra)) {
    return { tipo: null, digitos, valido: null, aviso: null };
  }
  if (letra) {
    return { tipo: 'rg', digitos, valido: null, aviso: null };
  }
  if (digitos > 11) {
    return {
      tipo: 'indefinido',
      digitos,
      valido: false,
      aviso: 'CPF tem 11 dígitos e o RG costuma ter até 9 — confira o número.',
    };
  }
  if (digitos === 11) {
    if (isValidCpf(texto)) {
      return { tipo: 'cpf', digitos, valido: true, aviso: null };
    }
    return {
      tipo: 'indefinido',
      digitos,
      valido: false,
      aviso: 'Tem 11 dígitos, mas o CPF não confere — confira os números (ou é RG?).',
    };
  }
  return { tipo: 'rg', digitos, valido: null, aviso: null };
}

/**
 * O MODO do campo (é ele que decide a máscara e o que o usuário vê):
 *   * começou com LETRA → 'rg'  (CPF nunca tem letra);
 *   * começou com NÚMERO → 'cpf' (a máscara de CPF aparece desde a 1ª tecla, e
 *     quem sabe que é RG clica em "é RG" — troca de 1 clique);
 *   * o escolhido à mão sempre manda no automático.
 * É diferente de `detectDocumento`, que diz o que o valor REALMENTE é.
 */
export function tipoDocumento(value, tipoForcado = 'auto') {
  if (tipoForcado === 'cpf' || tipoForcado === 'rg') return tipoForcado;
  const det = detectDocumento(value);
  if (det.tipo === null) return 'indefinido';
  if (det.tipo === 'rg' && temLetra(value)) return 'rg';
  if (det.digitos > 11) return 'rg';   // não corta nada: vira RG
  return 'cpf';
}

/**
 * A MÁSCARA do campo único, aplicada a cada tecla:
 *   * modo CPF → `000.000.000-00` (progressiva);
 *   * modo RG  → máscara leve (maiúsculas, sem inventar formato).
 */
export function smartMask(value, tipoForcado = 'auto') {
  const det = detectDocumento(value);
  if (det.tipo === null) return '';
  if (tipoDocumento(value, tipoForcado) === 'rg') return formatRg(value);
  return formatCpf(value);
}

/**
 * Quebra o campo único nos campos do cadastro (é o que o formulário envia).
 * Devolve sempre os TRÊS: o espelho `document` + `cpf` **ou** `rg` preenchido.
 *
 * Quem decide o destino é o que o valor REALMENTE é (não o modo da máscara):
 *   * 11 dígitos com verificador certo (ou em dúvida) → `cpf` — em dúvida vai
 *     como CPF para o aviso de validação aparecer;
 *   * RG → `rg`: com letra fica como foi digitado; só números é guardado SEM
 *     pontuação inventada (a máscara de CPF não "suja" o RG).
 */
export function documentoParaCampos(value, tipoForcado = 'auto') {
  const texto = String(value ?? '');
  const det = detectDocumento(texto);
  if (det.tipo === null) return { document: '', cpf: '', rg: '' };

  const forcado = tipoForcado === 'cpf' || tipoForcado === 'rg';
  let comoCpf;
  if (forcado) {
    comoCpf = tipoForcado === 'cpf' && det.digitos > 0;
  } else {
    comoCpf = det.tipo === 'cpf' || (det.tipo === 'indefinido' && det.digitos <= 11);
  }

  if (comoCpf) {
    const cpf = formatCpf(texto);
    return { document: cpf, cpf, rg: '' };
  }
  const rg = temLetra(texto) ? cleanRg(texto) : onlyDigits(texto);
  return { document: rg, cpf: '', rg };
}

/**
 * Texto do campo quando o usuário TROCA o tipo à mão ("é RG" / "é CPF" /
 * "automático"): a máscara nova é aplicada na hora, para o que está na tela ser
 * exatamente o que vai ser salvo. RG só de números perde a pontuação de CPF.
 */
export function textoAoEscolherTipo(value, tipoForcado) {
  const det = detectDocumento(value);
  if (det.tipo === null) return '';
  if (tipoForcado === 'auto') return smartMask(value, 'auto');
  if (tipoForcado === 'cpf') return formatCpf(value);
  return temLetra(value) ? cleanRg(value) : onlyDigits(value);
}

/**
 * Texto inicial do campo único ao abrir uma ficha: usa o CPF, senão o RG, senão
 * o valor do campo antigo `document` — que continua valendo para quem já estava
 * cadastrado (nada é perdido nem apagado).
 */
export function documentoDoRegistro(registro) {
  if (!registro) return '';
  if (registro.cpf) return formatCpf(registro.cpf);
  if (registro.rg) return formatRg(registro.rg);
  return smartMask(registro.document, tipoDoRegistro(registro));
}

/**
 * Modo com que o campo abre uma ficha JÁ EXISTENTE: se o que está gravado é um
 * RG (tem letra, ou é só número com até 10 dígitos), o campo já abre como RG —
 * em vez de assumir CPF e obrigar o usuário a clicar em "é RG" de novo.
 */
export function tipoDoRegistro(registro) {
  if (!registro) return 'auto';
  if (registro.cpf) return 'auto';
  if (registro.rg) return 'rg';
  return detectDocumento(registro.document).tipo === 'rg' ? 'rg' : 'auto';
}

/**
 * Troca os campos de tela (`documento` + `documento_tipo`) pelos campos que a
 * API entende (`document`, `cpf`, `rg`). Use em TODO envio de formulário.
 */
export function comCamposDeDocumento(dados) {
  const { documento, documento_tipo: tipo, ...resto } = dados || {};
  return { ...resto, ...documentoParaCampos(documento, tipo) };
}

/** Aplica a máscara do CPF ou do RG (usado no CPF do responsável). */
export function maskDocumento(value, tipo) {
  return tipo === 'cpf' ? formatCpf(value) : formatRg(value);
}

/** Valor final do documento, sem sobra nas pontas (ao sair do campo). */
export function cleanDocumento(value, tipo) {
  return tipo === 'cpf' ? formatCpf(value) : cleanRg(value);
}

/**
 * Aviso de CPF (ou null quando está vazio/válido).
 * O CPF é OPCIONAL: campo vazio nunca gera aviso.
 */
export function cpfWarning(value) {
  if (!onlyDigits(value)) return null;
  if (onlyDigits(value).length < 11) {
    return `CPF incompleto (${onlyDigits(value).length}/11 dígitos).`;
  }
  if (!isValidCpf(value)) {
    return 'CPF inválido — confira os números.';
  }
  return null;
}

/** Aviso quando o CPF do atleta é igual ao do responsável (troca de campos). */
export function sameCpfWarning(cpf, guardianCpf) {
  const a = onlyDigits(cpf);
  const b = onlyDigits(guardianCpf);
  if (!a || !b) return null;
  return a === b
    ? 'O CPF do atleta está igual ao do responsável — confira os campos.'
    : null;
}

/** Opções de parentesco do responsável legal. */
export const GUARDIAN_RELATIONSHIPS = ['Pai', 'Mãe', 'Avô/Avó', 'Tio/Tia', 'Irmão/Irmã', 'Tutor(a) legal', 'Outro'];
